import type { PublicationPackage } from './campaigns';
import type { Publication, SocialAccount } from './model';

/** Civil dates never pass through the browser's local timezone. */
export function calendarDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error('Data de calendário inválida.');
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(12, 0, 0, 0);
  if (date.toISOString().slice(0, 10) !== value) throw new Error('Data de calendário inválida.');
  return date;
}
const isoDay = (date: Date) => date.toISOString().slice(0, 10);
export function addCalendarDays(value: string, days: number) {
  const date = calendarDate(value);
  date.setUTCDate(date.getUTCDate() + days);
  return isoDay(date);
}
export function moveCalendarMonth(value: string, delta: number) {
  const date = calendarDate(value),
    day = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + delta);
  const end = new Date(date);
  end.setUTCMonth(end.getUTCMonth() + 1);
  end.setUTCDate(0);
  date.setUTCDate(Math.min(day, end.getUTCDate()));
  return isoDay(date);
}
export function calendarWeek(value: string) {
  const start = addCalendarDays(value, -((calendarDate(value).getUTCDay() + 6) % 7));
  return Array.from({ length: 7 }, (_, index) => addCalendarDays(start, index));
}
export function calendarMonth(value: string) {
  const first = `${value.slice(0, 7)}-01`,
    next = moveCalendarMonth(first, 1),
    last = addCalendarDays(next, -1);
  const start = calendarWeek(first)[0],
    end = calendarWeek(last)[6];
  const days: string[] = [];
  for (let day = start; day <= end; day = addCalendarDays(day, 1)) days.push(day);
  return days;
}
export function calendarLabel(
  value: string,
  options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' },
) {
  return new Intl.DateTimeFormat('pt-BR', { ...options, timeZone: 'UTC' }).format(
    calendarDate(value),
  );
}
export function publicationCalendarDay(
  publication: Pick<Publication, 'date' | 'precision' | 'publishedAt'>,
) {
  if (publication.precision !== 'second' || !publication.publishedAt) return publication.date;
  const instant = new Date(publication.publishedAt);
  return Number.isNaN(instant.getTime())
    ? publication.date
    : new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Sao_Paulo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(instant);
}
export interface CalendarAccount {
  key: string;
  channel: string;
  account: string;
  verified: boolean;
  contentIds: string[];
  sources: ('publication' | 'destination')[];
}
export interface CalendarAccounts {
  options: CalendarAccount[];
  publicationKeys: Record<string, string>;
  contentKeys: Record<string, string[]>;
}
const normalize = (value: string) => value.trim().replace(/^@/, '').toLocaleLowerCase('pt-BR');
const channelKey = (value: string) => value.trim().toLocaleLowerCase('pt-BR');
const aliasKey = (channel: string, account: string) =>
  JSON.stringify([channelKey(channel), normalize(account)]);
/** Uses recorded destinations and publications only; a coauthor or a display name is never a destination. */
export function calendarAccounts(
  publications: Publication[],
  packages: PublicationPackage[],
  connected: SocialAccount[] = [],
): CalendarAccounts {
  const aliases = new Map<string, Set<string>>(),
    verified = new Set<string>();
  const accountId = (channel: string, id: string) => {
    const match = connected.find(
      (account) =>
        channelKey(account.platform) === channelKey(channel) &&
        [account.id, account.externalId].includes(id),
    );
    return JSON.stringify([channelKey(channel), 'id', match?.externalId ?? id]);
  };
  const addAlias = (channel: string, account: string, id: string) => {
    if (!account.trim()) return;
    const key = aliasKey(channel, account),
      ids = aliases.get(key) ?? new Set<string>();
    ids.add(id);
    aliases.set(key, ids);
  };
  for (const account of connected) {
    const key = accountId(account.platform, account.externalId);
    verified.add(key);
    if (account.username) addAlias(account.platform, account.username, key);
    addAlias(account.platform, account.externalId, key);
  }
  for (const publication of publications)
    if (publication.accountId)
      addAlias(
        publication.channel,
        publication.account,
        accountId(publication.channel, publication.accountId),
      );
  const resolveKey = (channel: string, account: string, id?: string) => {
    if (id) return accountId(channel, id);
    const options = aliases.get(aliasKey(channel, account));
    return options?.size === 1
      ? [...options][0]
      : JSON.stringify([channelKey(channel), 'recorded', normalize(account)]);
  };
  const options = new Map<string, CalendarAccount>(),
    contentKeys: Record<string, string[]> = {},
    publicationKeys: Record<string, string> = {};
  const add = (
    channel: string,
    account: string,
    contentId: string,
    source: CalendarAccount['sources'][number],
    id?: string,
  ) => {
    if (!channel.trim() || !account.trim()) return '';
    const key = resolveKey(channel, account, id),
      item = options.get(key) ?? {
        key,
        channel,
        account,
        verified: verified.has(key),
        contentIds: [],
        sources: [],
      };
    if (!item.contentIds.includes(contentId)) item.contentIds.push(contentId);
    if (!item.sources.includes(source)) item.sources.push(source);
    options.set(key, item);
    contentKeys[contentId] ??= [];
    if (!contentKeys[contentId].includes(key)) contentKeys[contentId].push(key);
    return key;
  };
  for (const publication of publications)
    publicationKeys[publication.id] = add(
      publication.channel,
      publication.account,
      publication.contentId,
      'publication',
      publication.accountId,
    );
  for (const pkg of packages.filter((value) => !['recusada', 'substituida'].includes(value.status)))
    for (const destination of pkg.structuredDestinations ?? [])
      add(destination.channel, destination.account, pkg.contentId, 'destination');
  return {
    options: [...options.values()].sort((a, b) =>
      `${a.channel} ${a.account}`.localeCompare(`${b.channel} ${b.account}`, 'pt-BR'),
    ),
    contentKeys,
    publicationKeys,
  };
}
